import { useState, useRef, useEffect, useImperativeHandle, forwardRef } from "react";
import { Loader2, AlertCircle, ExternalLink } from "lucide-react";

interface YouTubeEmbedProps {
  embedUrl: string;
  title: string;
  videoId: string;
}

export interface YouTubePlayerRef {
  pause: () => void;
  play: () => void;
  getCurrentTime: () => Promise<number>;
  getPlayerState: () => Promise<number>;
  seekTo: (seconds: number) => void;
}
// YouTube API types are declared in src/vite-env.d.ts

export const YouTubeEmbed = forwardRef<YouTubePlayerRef, YouTubeEmbedProps>(({ embedUrl, title, videoId }, ref) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const playerRef = useRef<any>(null);
  const [isPlayerReady, setIsPlayerReady] = useState(false);

  // Load YouTube IFrame API
  useEffect(() => {
    if (typeof window !== 'undefined' && !window.YT) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);
    }
  }, []);

  // Initialize YouTube player when API is ready
  useEffect(() => {
    const initPlayer = () => {
      if (window.YT && window.YT.Player && iframeRef.current) {
        playerRef.current = new window.YT.Player(iframeRef.current, {
          events: {
            onReady: () => {
              setIsPlayerReady(true);
              setIsLoading(false);
              setHasError(false);
            },
            onError: () => {
              setIsLoading(false);
              setHasError(true);
            },
            onStateChange: (event: any) => {
              // Handle state changes (play, pause, etc.)
              if (event.data === window.YT.PlayerState.PLAYING) {
                // Notify parent that video is playing
                window.dispatchEvent(new CustomEvent('youtube-playing', { 
                  detail: { videoId, currentTime: playerRef.current?.getCurrentTime() } 
                }));
              } else if (event.data === window.YT.PlayerState.PAUSED) {
                // Notify parent that video is paused
                window.dispatchEvent(new CustomEvent('youtube-paused', { 
                  detail: { videoId, currentTime: playerRef.current?.getCurrentTime() } 
                }));
              }
            }
          }
        });
      }
    };

    if (window.YT && window.YT.Player) {
      initPlayer();
    } else {
      window.onYouTubeIframeAPIReady = initPlayer;
    }

    return () => {
      if (playerRef.current && typeof playerRef.current.destroy === 'function') {
        playerRef.current.destroy();
      }
    };
  }, [videoId]);

  // Expose player methods through ref
  useImperativeHandle(ref, () => ({
    pause: () => {
      if (playerRef.current && typeof playerRef.current.pauseVideo === 'function') {
        playerRef.current.pauseVideo();
      }
    },
    play: () => {
      if (playerRef.current && typeof playerRef.current.playVideo === 'function') {
        playerRef.current.playVideo();
      }
    },
    getCurrentTime: async () => {
      if (playerRef.current && typeof playerRef.current.getCurrentTime === 'function') {
        return playerRef.current.getCurrentTime();
      }
      return 0;
    },
    getPlayerState: async () => {
      if (playerRef.current && typeof playerRef.current.getPlayerState === 'function') {
        return playerRef.current.getPlayerState();
      }
      return -1; // UNSTARTED
    },
    seekTo: (seconds: number) => {
      if (playerRef.current && typeof playerRef.current.seekTo === 'function') {
        playerRef.current.seekTo(seconds, true);
      }
    }
  }), []);

  const handleLoad = () => {
    if (!isPlayerReady) {
      setIsLoading(false);
      setHasError(false);
    }
  };

  const handleError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  const openInYouTube = () => {
    window.open(`https://www.youtube.com/watch?v=${videoId}`, '_blank');
  };

  return (
    <div className="absolute inset-0 w-full h-full">
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-100 rounded-lg">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin text-slate-600 mx-auto mb-2" />
            <p className="text-sm text-slate-600">Loading video...</p>
          </div>
        </div>
      )}
      
      {hasError && (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-100 rounded-lg">
          <div className="text-center p-6">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">Unable to load video</h3>
            <p className="text-sm text-slate-600 mb-4">
              The video might be restricted or unavailable for embedding.
            </p>
            <button
              onClick={openInYouTube}
              className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
              Open in YouTube
            </button>
          </div>
        </div>
      )}
      
      <iframe
        ref={iframeRef}
        src={`${embedUrl}?enablejsapi=1&origin=${window.location.origin}`}
        title={title}
        className="absolute inset-0 w-full h-full border-0 rounded-lg"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
        onLoad={handleLoad}
        onError={handleError}
        style={{ display: hasError ? 'none' : 'block' }}
      />
      
      {!isLoading && !hasError && (
        <div className="absolute top-4 right-4 z-10">
          <button
            onClick={openInYouTube}
            className="p-2 bg-black/50 text-white rounded-lg hover:bg-black/70 transition-colors backdrop-blur-sm"
            title="Open in YouTube"
          >
            <ExternalLink className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
});

YouTubeEmbed.displayName = 'YouTubeEmbed';